import { TYPES, SignUpActions } from 'store/modules/sign-up/actions';
import {
  IWorkItOutData, IPickAPlanData, PromoCodeDiscount, IContactDetails, ICompanyDetails, IPayroll, ISummary,
} from 'types';

const USE_MOCK_DATA = false;

interface SignUpReducerState {
  workItOut: IWorkItOutData;
  pickAPlan: IPickAPlanData;

  isCharity: boolean;

  defaultPromoCode?: string;
  promoCode: {
    isLoading: boolean;
    isSuccess: boolean;
    isFailure: boolean;

    discount?: PromoCodeDiscount;
    error?: string;
  }

  contactDetails?: IContactDetails;
  companyDetails?: ICompanyDetails;
  payrollInformation?: IPayroll;

  summary: ISummary;

  isSubmitLoading: boolean;
  submitSuccess: boolean;
  submitError: string;
  hasSubmitted: boolean;
}

let initialState: SignUpReducerState = {
  workItOut: {
    payRuns: [],
    manageHr: '',
  },

  pickAPlan: {
    chosenPlan: '',
    extras: [],
  },

  isCharity: false,

  promoCode: {
    isLoading: false,
    isSuccess: false,
    isFailure: false,

    discount: undefined,
    error: undefined,
  },

  contactDetails: {},

  summary: {
    additionalComments: '',
    howDidYouHearAboutUs: '',
    sendMeInformation: true,
    howDidYouHearAboutUsOther: '',
    referralCode: '',
  },

  isSubmitLoading: false,
  submitSuccess: false,
  submitError: '',
  hasSubmitted: false,
};

if (process.env.NODE_ENV === 'development' && USE_MOCK_DATA) {
  initialState = {
    workItOut: {
      payRuns: [{
        employeeCount: 15, payCycle: 'Fortnightly',
      }, {
        employeeCount: 40, payCycle: 'Monthly',
      }],
      managePayCycles: 'calc-only',
      manageHr: 'We use an external HR company/software',
      bankChoice: {
        smartPayroll: true,
        isCalculatorOnly: true,
        isFullService: true,
        id: 'e1c2ec17-a3c4-ea11-a812-000d3a6a398b',
        level2: true,
        name: 'ANZ',
        processingFeeApplies: false,
        isBankOther: false,
      },
    },
    pickAPlan: {
      chosenPlan: 'calculate-and-automate',
      extras: ['smart-hr'],
    },

    isCharity: false,
    promoCode: {
      isLoading: false,
      isSuccess: false,
      isFailure: false,

      discount: undefined,
      error: undefined,
    },

    contactDetails: {
      contact: {
        firstName: 'Joe',
        lastName: 'Blogs',
        email: 'blogs@test.com',
        contactNumber: '123 456 789',
        jobTitle: 'Chairperson',
      },
      isAccountant: false,
    },

    companyDetails: {
      companyName: 'Test',
      tradingName: 'Alpha',
      irdNumber: '12345678',
      industryType: 'Mining',
      charityRegistrationNumber: '',
      accountant: {
        name: 'ABA Chaucer Ltd',
        id: 'ca127f9f-158d-e211-8d2a-005056ae0100',
      },
      accountantBusinessName: '',
      accountantPhoneNumber: '',
      accountantHasAccessTo: 'Processing payroll',
      accountantName: 'Greg the accountant',
      accountantWillPaySmartPayrollFees: true,
      shouldAddToAccountantsUmbrellaPage: false,
      umbrellaPageName: '',
      postalSameAsBusiness: true,
    },

    payrollInformation: {
      bankAccountName: 'My bank name',
      bankAccountNumber: '1234123412341234',
      authoriseDirectDebitMonthlyFees: true,
      notAuthorisedButRequestDirectDebitAuthority: false,
      processingPayroll: 'Other',
      processingPayrollOther: "Here's my other field yo",
      payePaymentsToIrd: 'Once a month (small employer)',
      employeeAnnualLeaveRollover: 'Company Anniversary Date',
      averageGrossAmountPerPayRun: 2000,
      payrollProcessingStart: '20/02/2020',
    },

    summary: {
      additionalComments: 'This is a test',
      howDidYouHearAboutUs: 'Franchisor',
      sendMeInformation: true,
      howDidYouHearAboutUsOther: '',
      referralCode: 'referral_code',
    },

    isSubmitLoading: false,
    submitSuccess: false,
    submitError: '',
    hasSubmitted: false,
  };
}

export const signUpReducer = (state: SignUpReducerState = initialState, action: SignUpActions): SignUpReducerState => {
  switch (action.type) {
    case TYPES.SET_WORK_IT_OUT:
      return {
        ...state,
        workItOut: action.payload,
        // Reset pick a plan fields if something changes on the work it out step
        pickAPlan: {
          chosenPlan: '',
          extras: [],
        },
      };

    case TYPES.SET_PICK_A_PLAN:
      return {
        ...state,
        pickAPlan: action.payload,
      };

    case TYPES.SET_DEFAULT_PROMO_CODE:
      return {
        ...state,
        defaultPromoCode: action.payload.promoCode,
      };

    case TYPES.CLEAR_PROMO_CODE_STATUS:
      return {
        ...state,
        defaultPromoCode: undefined,
        promoCode: {
          isLoading: false,
          discount: state.promoCode.discount,

          isSuccess: state.promoCode.isSuccess,
          isFailure: false,
          error: undefined,
        },
      };

    case TYPES.APPLY_PROMO_CODE:
      return {
        ...state,
        promoCode: {
          isLoading: true,
          discount: undefined,

          isSuccess: false,
          isFailure: false,
          error: undefined,
        },
        summary: {
          ...state.summary,
          referralCode: '',
        },
      };
    case TYPES.APPLY_PROMO_CODE_SUCCESS:
      return {
        ...state,
        promoCode: {
          isLoading: false,
          discount: action.payload.promoCodeDiscount,

          isSuccess: true,
          isFailure: false,
          error: undefined,
        },
      };
    case TYPES.APPLY_PROMO_CODE_FAILURE:
      return {
        ...state,
        promoCode: {
          isLoading: false,
          discount: undefined,

          isSuccess: false,
          isFailure: true,
          error: action.payload.promoCodeError,
        },
      };

      // =====================
      // Referral code
      // =====================

    case TYPES.APPLY_REFERRAL_CODE:
      return {
        ...state,
        summary: {
          ...state.summary,
          referralCode: action.payload.referralCode,
        },
      };

    case TYPES.SET_IS_CHARITY:
      return {
        ...state,
        isCharity: action.payload,
      };

    case TYPES.SET_CONTACT_DETAILS:
      return {
        ...state,
        contactDetails: action.payload,
      };

    case TYPES.SET_ACCOUNTANT_DETAILS:
      return {
        ...state,
        companyDetails: {
          ...state.companyDetails || {},
          ...action.payload,
        },
      };

    case TYPES.SET_COMPANY_DETAILS:
      return {
        ...state,
        companyDetails: action.payload,
      };

    case TYPES.SET_PAYROLL:
      return {
        ...state,
        payrollInformation: action.payload,
      };

    case TYPES.SET_SUMMARY:
      return {
        ...state,
        summary: action.payload,
      };

    case TYPES.SUBMIT:
      return {
        ...state,
        isSubmitLoading: true,
        submitSuccess: false,
        submitError: '',
      };

    case TYPES.SUBMIT_SUCCESS:
      return {
        ...state,
        isSubmitLoading: false,
        submitSuccess: true,
        hasSubmitted: true,
        submitError: '',
      };
    case TYPES.SUBMIT_FAILURE:
      return {
        ...state,
        isSubmitLoading: false,
        submitError: action.payload.error,
        hasSubmitted: true,
      };
    case TYPES.SUBMIT_CLEAR:
      return {
        ...state,
        isSubmitLoading: false,
        submitError: '',
        hasSubmitted: false,
      };
    default:
      return state;
  }
};
